// ==========================================
// ENUM MAPPINGS BACKEND
// ==========================================

export const ActivityTypeNum = { Social: 1, Sport: 2, Help: 3 } as const;
export type ActivityTypeNum = typeof ActivityTypeNum[keyof typeof ActivityTypeNum];

export const HelpTypeNum = { None: 0, Request: 1, Offer: 2 } as const;
export type HelpTypeNum = typeof HelpTypeNum[keyof typeof HelpTypeNum];

export const CoinPostCategoryNum = { Vu: 1, Confession: 2, CoupDeCoeur: 3, Question: 4 } as const;
export type CoinPostCategoryNum = typeof CoinPostCategoryNum[keyof typeof CoinPostCategoryNum];

// ==========================================
// FRONTEND ACTIVITY
// ==========================================

export type ActivityType = 'SOCIAL' | 'SPORT' | 'HELP_REQUEST' | 'HELP_OFFER';

export interface Activity {
  id: string;
  type: ActivityType;
  title: string;
  description: string;
  organizer: string;
  time: string;
  endsAt?: string | null;
  nextSessionStartsAt?: string | null;
  upcomingSessionsCount?: number;
  timeCategory: 'TODAY' | 'WEEK' | 'UPCOMING';
  distanceKm: number;
  location: string;
  latitude?: number | null;
  longitude?: number | null;
  participants: number;
  maxParticipants: number | null;
  commentsCount: number;
  tags: string[];
  isOwner?: boolean;

  createdAt: string;
}

// ==========================================
// AUTH DTOs
// ==========================================

export interface RegisterDto {
  email: string;
  displayName: string;
  password: string;
  isAtLeast17: boolean;
}

export interface LoginDto {
  email: string;
  password: string;
}

export interface RefreshTokenDto {
  refreshToken: string;
}

export interface AuthResponse {
  accessToken?: string;
  token?: string;
  refreshToken?: string;
  refresh_token?: string;
  user?: ApiUser;
}

export interface ApiUser {
  id: string;
  email: string;
  displayName?: string;
  name?: string;
  emailConfirmed?: boolean;
  avatarUrl?: string | null;
  bio?: string | null;
  isAdmin?: boolean;
  isModerator?: boolean;
  isBanned?: boolean;
  roles?: string[];
}

// ==========================================
// ACTIVITY DTOs
// ==========================================

export interface CreateActivityDto {
  type: ActivityTypeNum;
  helpType?: HelpTypeNum;
  title: string;
  description?: string | null;
  publicLocationLabel?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  startsAt?: string | null;
  endsAt?: string | null;
  maxParticipants?: number | null;
  tags?: string[]
}


export interface ReprogramActivityDto {
  startsAt: string;
  endsAt?: string | null;
}


export interface CreateRecurringActivitiesDto {
  startsAt: string;
  endsAt?: string | null;
  intervalWeeks: number;
  occurrences: number;
}


export interface ApiActivity {
  id: string;
  type: ActivityTypeNum;
  helpType?: HelpTypeNum;
  title: string;
  description?: string | null;
  publicLocationLabel?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  startsAt?: string | null;
  endsAt?: string | null;
  nextSessionStartsAt?: string | null;
  upcomingSessionsCount?: number;
  maxParticipants?: number | null;
  participantCount?: number;
  participantsCount?: number;
  isJoined?: boolean;
  isOwner?: boolean;
  author?: ApiUser;
  createdAt: string;
  distanceKm?: number | null;
  tags?: string[];
  commentsCount?: number;
}

export interface ApiActivitySession {
  id: string;
  startsAt: string;
  endsAt?: string | null;
}

export interface ApiActivityDetail extends ApiActivity {
  participants?: ApiUser[];
  comments?: ApiActivityComment[];
  sessions?: ApiActivitySession[];
  isJoined: boolean;
  isOwner: boolean;
  isFull: boolean;
  placesRemaining: number | null;
}

export interface ApiActivityComment {
  id: string;
  author: ApiUser;
  content: string;
  createdAt: string;
  isOwner?: boolean;
}

export interface ActivityFeedParams {
  Type?: ActivityTypeNum;
  HelpType?: HelpTypeNum;
  Latitude?: number;
  Longitude?: number;
  RadiusKm?: number;
  FromDate?: string;
  Time?: 'TODAY' | 'WEEK' | 'UPCOMING';
  Page?: number;
  PageSize?: number;
}

// ==========================================
// COIN POST DTOs
// ==========================================

export interface CreateCoinPostDto {
  category: CoinPostCategoryNum;
  content: string;
  isAnonymous?: boolean;
  cityId?: number | null;
  areaLabel?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

export interface ApiCoinPost {
  id: string;
  category: CoinPostCategoryNum;
  content: string;
  isAnonymous: boolean;
  areaLabel?: string | null;
  authorAlias: string;
  reactionsCount: number;
  hasReacted?: boolean;
  commentsCount: number;
  createdAt: string;
}

export interface ApiCoinComment {
  id: string;
  authorAlias: string;
  content: string;
  createdAt: string;
  isOwner: boolean;
  author: ApiUser;
}

export interface DiscussionMessage {
  id: string;
  author: string;
  avatarUrl?: string | null;
  text: string;
  time: string;
  isOwner?: boolean;
}

export interface CoinFeedParams {
  Category?: CoinPostCategoryNum;
  CityId?: number;
  Latitude?: number;
  Longitude?: number;
  RadiusKm?: number;
  Page?: number;
  PageSize?: number;
}

// ==========================================
// FRONTEND COIN
// ==========================================

export type CoinCategory = 'VU' | 'CONFESSION' | 'COUP_DE_COEUR' | 'QUESTION';
export type CoinCity = 'DOLE' | 'LONS' | 'JURA';

export interface CoinComment {
  id: string;
  authorAlias: string;
  content: string;
  time: string;
  isOwner?: boolean;
}

export interface CoinPost {
  id: string;
  category: CoinCategory;
  content: string;
  authorAlias: string;
  city: CoinCity;
  time: string;
  likes: number;
  comments: CoinComment[];
  hasReacted?: boolean;
  commentCount?: number;
  createdAt: string;
}

// ==========================================
// USER PROFILE
// ==========================================

export interface UserProfileActivity {
  id: string;
  title: string;
  type: ActivityType;
  time: string;
  role: 'organisateur' | 'participant';
}

export interface UserProfile {
  id: string;
  email: string;
  displayName: string;
  roles: string[];
  isBanned: boolean;
  banReason?: string | null;
  bannedUntil?: string | null;
  createdAt: string;
  bio: string;
  city: string;
  avatarUrl: string;
  activitiesCount: number;
  participationsCount: number;
  activities: UserProfileActivity[];
}

// ==========================================
// NOTIFICATION DTOs
// ==========================================

export interface ApiNotification {
  id: string;
  type: string;
  message: string;
  isRead: boolean;
  createdAt: string;
  entityId?: string | null;
  entityType?: string | null;
}

// ==========================================
// ADMIN DTOs
// ==========================================

export interface ApiReport {
  id: string;
  entityType: string;
  entityId: string;
  reason: string;
  status: string;
  createdAt: string;
  reporter?: ApiUser;
  reporterName?: string | null;
  reporterAlias?: string | null;
  content?: string | null;
  reportedContent?: string | null;
  resolvedAt?: string | null;
}

export interface ApiAdminUser extends ApiUser {
  createdAt?: string;
  isBanned: boolean;
  banReason?: string | null;
  bannedUntil?: string | null;
  roles?: string[];
}

export interface BanUserDto {
  reason: string;
  bannedUntil?: string | null;
}

export interface ChangeUserRoleDto {
  role: string;
}

// ==========================================
// LOCALISATION
// ==========================================

export interface SelectedPlace {
  label: string;
  latitude: number;
  longitude: number;
}